// Platform and tenant admins can open the customer-scoped Queue/AA/CQ view.
// branch_manager and end_user need an actual branch_ids entry
// (branch_manager via explicit assignment, end_user via their own DID's
// customer_numbers.branch_id). View As rewrites `role`, so an emulating
// platform admin continues to receive the target role's access here.
export function hasQueueAccess(
  user: { role?: string; branch_ids?: number[] } | null | undefined,
): boolean {
  if (!user) return false;
  if (user.role === 'platform_admin' || user.role === 'tenant_admin') return true;
  return (user.branch_ids?.length ?? 0) > 0;
}

export function hasDashboardAccess(
  user: { role?: string; actual_role?: string } | null | undefined,
): boolean {
  return !!user && (
    (user.role === 'platform_admin' && user.actual_role === 'platform_admin')
    || (user.role === 'tenant_admin'
      && (user.actual_role === 'tenant_admin' || user.actual_role === 'platform_admin'))
  );
}

// Company analytics is an internal prototype. Keep it available to an
// un-emulated Logic V platform administrator for demonstrations, but do not
// expose it as a customer-tenant capability yet.
export function hasCompanyAccess(
  user: { role?: string; actual_role?: string } | null | undefined,
): boolean {
  return user?.role === 'platform_admin' && user.actual_role === 'platform_admin';
}

// Interaction Detail journey (owner send-back F5c): only a real,
// un-emulated Logic V platform admin sees the Carrier/SBC/Teams
// infrastructure tier. While View As is active, `role` carries the
// emulated role and `actual_role` stays `platform_admin` -- checking
// `actual_role` alone (the pre-fix bug) leaked infrastructure nodes into
// the emulated end user's own view. Same "role === actual_role" idiom as
// `hasDashboardAccess`/`hasCompanyAccess` above.
export function canSeeInfrastructureTier(
  user: { role?: string; actual_role?: string } | null | undefined,
): boolean {
  return user?.role === 'platform_admin' && user.actual_role === 'platform_admin';
}

export function customerBranding(
  user: {
    customer_display_name?: string | null;
    customer_filter?: string | null;
    customer_logo_data_url?: string | null;
  } | null | undefined,
): { displayName: string; logoDataUrl: string | null } {
  return {
    displayName: user?.customer_display_name || user?.customer_filter || '',
    logoDataUrl: user?.customer_logo_data_url || null,
  };
}

export function accessFallbackRoute(
  user: { role?: string; actual_role?: string } | null | undefined,
): '/dashboard' | '/branch' | '/my' {
  if (user?.role === 'platform_admin' && user.actual_role === 'platform_admin') return '/dashboard';
  if (user?.role === 'branch_manager') return '/branch';
  return '/my';
}

// Branch/location dashboard: platform_admin, tenant_admin and branch_manager
// all see the rolled-up view for their scope.
export function hasBranchDashboardAccess(
  user: { role?: string } | null | undefined,
): boolean {
  return (
    user?.role === 'platform_admin' ||
    user?.role === 'tenant_admin' ||
    user?.role === 'branch_manager'
  );
}

// Settings is tenant_admin only -- not platform_admin. LogicV's own
// permissions live in the app registration itself, not a per-customer
// consent link.
export function hasSettingsAccess(
  user: { role?: string } | null | undefined,
): boolean {
  return user?.role === 'tenant_admin';
}

// Whether this person can open the "View As" emulation control at all
// (shared by the header trigger button and PlatformEmulationControl so
// they can't disagree about who sees the button). Gated on `actual_role`,
// not `role`, so it stays available while already emulating.
export function hasEmulationAccess(
  user: { actual_role?: string } | null | undefined,
): boolean {
  return user?.actual_role === 'platform_admin' || user?.actual_role === 'tenant_admin';
}

export interface NavItem {
  path: string;
  label: string;
  visible: boolean;
}

type NavUser = {
  role?: string;
  actual_role?: string;
  branch_ids?: number[];
  can_access_recordings?: boolean;
  location_label_plural?: string;
} | null | undefined;

// Single source of truth for app navigation (spec §4.2, findings F7). Both
// the desktop button row (AppNav) and PageHeader's mobile drawer render this
// same list so they can never disagree again.
export function buildNavItems(user: NavUser): NavItem[] {
  const locationPlural = user?.location_label_plural || 'Branches';

  return [
    { path: '/dashboard', label: 'Dashboard', visible: hasDashboardAccess(user) },
    { path: '/my', label: 'My Calls', visible: true },
    { path: '/calls', label: 'Call History', visible: true },
    { path: '/branch', label: locationPlural, visible: hasBranchDashboardAccess(user) },
    { path: '/recordings', label: 'Recordings', visible: user?.can_access_recordings === true },
    { path: '/company', label: 'Company', visible: hasCompanyAccess(user) },
    { path: '/queues', label: 'Queues', visible: hasQueueAccess(user) },
    { path: '/settings', label: 'Settings', visible: hasSettingsAccess(user) },
  ].filter((item) => item.visible);
}

// Where "/" sends a signed-in user. Decided with the owner 2026-09-20:
// platform admins land on their own calls (the all-customers dashboard was
// scanning every tenant on every sign-in, 20 s+ and growing); tenant admins
// are the technical audience and land on their (single-customer) dashboard;
// anyone with a queue scope lands on Queues; everyone else on My Calls.
export function homeRoute(
  user: { role?: string; branch_ids?: number[] } | null | undefined,
): '/my' | '/dashboard' | '/queues' {
  if (!user) return '/my';
  if (user.role === 'platform_admin') return '/my';
  if (user.role === 'tenant_admin') return '/dashboard';
  return hasQueueAccess(user) ? '/queues' : '/my';
}

